// The date-aware schedule sentence shown at the top of the Splash tab.
//
// The tab used to print one hardcoded sentence ("…only available on weekends
// from May 23 - June 21"), which was correct for four weeks and misleading for
// the rest of the summer: on July 31 it still told families the pads were
// weekend-only. This module derives the sentence from the season data instead,
// so it always describes TODAY.
//
// ANTI-FABRICATION: every date printed here comes from the catalog
// (`SPLASH_SEASON.weekendsOnly` / `.daily`, transcribed from `schedule2026`),
// and every hours/season phrase comes from `SPLASH_SEASON.hours` / `.typical`.
// Nothing is inferred. If a city's season carries no machine-readable windows,
// the function falls back to printing that city's own `schedule2026` string
// verbatim rather than guessing a rule.

import type { SplashSeason } from "../shared/types";
import { SPLASH_SEASON } from "./splash-pads";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** "2026-06-21" → "June 21". Returns the input unchanged if it isn't an ISO date. */
export function formatSeasonDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const month = MONTHS[Number(m[2]) - 1];
  if (!month) return iso;
  return `${month} ${Number(m[3])}`;
}

/** Local calendar date of `d` as "YYYY-MM-DD" (comparable to the season ISO dates). */
function localIsoDate(d: Date): string {
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The tail of the season descriptor: "Memorial Day weekend through Labor Day
 * weekend" → "Labor Day weekend". Lets the daily-phase sentence end with the
 * city's own words for when the season closes without hardcoding a holiday.
 */
export function seasonEndPhrase(typical: string): string {
  const idx = typical.toLowerCase().lastIndexOf(" through ");
  return idx === -1 ? typical.trim() : typical.slice(idx + " through ".length).trim();
}

/**
 * The schedule sentence for `now`, in four phases:
 *
 *  - before the season      → when it starts, and that it opens weekends-only
 *  - weekends-only shoulder → the weekend rule + when daily hours begin
 *  - daily run              → "Open daily, <hours>, through <season end phrase>."
 *  - after the season       → that it has ended, plus the typical season
 *
 * Pure: pass `now` to test any phase. Defaults to the DC catalog season.
 */
export function splashSeasonLine(
  now: Date = new Date(),
  season: SplashSeason = SPLASH_SEASON,
): string {
  const { weekendsOnly, daily, hours, typical, schedule2026 } = season;
  // No machine-readable windows for this city — print its own prose, unchanged.
  if (!weekendsOnly || !daily) return schedule2026;

  const today = localIsoDate(now);
  const dailyStart = formatSeasonDate(daily.start);

  if (today < weekendsOnly.start) {
    return `The season starts ${formatSeasonDate(weekendsOnly.start)}: weekends only, ${hours}, until daily hours begin ${dailyStart}.`;
  }
  if (today <= weekendsOnly.end) {
    return `Weekends only right now, ${hours}. Daily hours begin ${dailyStart}.`;
  }
  if (today < daily.start) {
    return `Daily hours, ${hours}, begin ${dailyStart}.`;
  }
  if (today <= daily.end) {
    return `Open daily, ${hours}, through ${seasonEndPhrase(typical)}.`;
  }
  return `The season ended ${formatSeasonDate(daily.end)}. Spray parks typically run ${typical}.`;
}
